import { Table, InputNumber, Button, Typography } from "antd";
import { useContext, useEffect, useState } from "react";
import TabsHeader from "../../../../Components/Shared/TabsHeader";
import { useParams } from "react-router-dom";
import { GetYearByRegister } from "../../../../API/RegisterAPI";
import { InvoicingFinep, InvoicingFinepForm } from "../../../../Models/InvoicingFinepModel";
import { useForm } from "react-hook-form";
import { GetInvoicingsFinep, PostInvoicingFinep, PutInvoicingFinep, PutValidateInvoicingFinep } from "../../../../API/InvoicingFinepAPI";
import { useAuth } from "../../../../Hooks/useAuth";
import Loader from "../../../../Components/Shared/Loader";
import { convertCurrencyToFloat } from "../../../../Utils/convertCurrencyToFloat";
import { RegistrationDocumentsFinepContext } from "../../../../Contexts/RegistrationDocumentsFinepContext";
import type { ColumnsType } from "antd/es/table";
import { debounce } from "lodash";
import { PlusOutlined } from "@ant-design/icons";
import "./styles.css";

const { Title, Text } = Typography;

type ObjectFormProps = {
    title: string;
    name:
    | "grossOperatingRevenue"
    | "servicesProvided"
    | "internMarketServices"
    | "externalMarketServices"
    | "productSales"
    | "internMarketProduct"
    | "externalMarketProduct"
    | "deductions"
    | "netRevenue";
    bold: boolean;
    h5: boolean;
};

type FieldPath =
    | `year.${keyof InvoicingFinepForm['year']}`
    | `lastYear.${keyof InvoicingFinepForm['lastYear']}`
    | `yearBeforeLast.${keyof InvoicingFinepForm['yearBeforeLast']}`;

const objectForm: ObjectFormProps[] = [
    { title: "Receita Operacional Bruta (ROB)", name: "grossOperatingRevenue", bold: true, h5: true },
    { title: "Serviços Prestados", name: "servicesProvided", bold: true, h5: false },
    { title: "Mercado Interno", name: "internMarketServices", bold: false, h5: false },
    { title: "Mercado Externo", name: "externalMarketServices", bold: false, h5: false },
    { title: "Vendas de Produtos", name: "productSales", bold: true, h5: false },
    { title: "Mercado Interno", name: "internMarketProduct", bold: false, h5: false },
    { title: "Mercado Externo", name: "externalMarketProduct", bold: false, h5: false },
    { title: "Deduções", name: "deductions", bold: true, h5: false },
    { title: "Receita Líquida", name: "netRevenue", bold: true, h5: true },
];

const FinancialOpeningsTab = () => {
    const [year, setYear] = useState(0);
    const [loading, setLoading] = useState(false);
    const [validateFields, setValidateFields] = useState(false);
    const { registerId } = useParams();
    const user = useAuth();
    const { register, handleSubmit, setValue, getValues, reset } = useForm<InvoicingFinepForm>();
    const { changeTab, handleFillTab, handleValidateTab, handleInvalidateTab } = useContext(RegistrationDocumentsFinepContext);

    useEffect(() => {
        async function getFinancialData() {
            setLoading(true);
            try {
                const yearResponse = await GetYearByRegister(registerId ?? "");

                setYear(yearResponse.data);

                const invoicingResponse = await GetInvoicingsFinep(registerId ?? "");

                if (invoicingResponse.length > 0) {
                    
                    const resultsOrdered = invoicingResponse.sort((a, b) => a.year - b.year);
                    
                    const formValues: InvoicingFinepForm = {
                        yearBeforeLast: resultsOrdered[0],
                        lastYear: resultsOrdered[1],
                        year: resultsOrdered[2]
                    };
                    
                    reset(formValues);
                    const hasTrue = invoicingResponse.some(x => x.isValid === true);
                    setValidateFields(hasTrue);
                    handleValidateTab(2);
                    handleFillTab(2);
                } else {
                  
                    reset({
                        yearBeforeLast: initEmptyYear(yearResponse.data - 2),
                        lastYear: initEmptyYear(yearResponse.data - 1),
                        year: initEmptyYear(yearResponse.data)
                    });
                }
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        }
        
        const initEmptyYear = (year: number): InvoicingFinep => ({
            year,
            registerId: registerId ?? "",
            userEmail: user.userEmail,
            grossOperatingRevenue: 0,
            servicesProvided: 0,
            internMarketServices: 0,
            externalMarketServices: 0,
            productSales: 0,
            internMarketProduct: 0,
            externalMarketProduct: 0,
            deductions: 0,
            netRevenue: 0,
            isValid: false,
            id: ""
        });
        getFinancialData();
    }, [registerId, user.userEmail, reset]);

    const transformFormValuesToNumber = (data: InvoicingFinepForm) => {
        objectForm.forEach(x => {
            data.lastYear[x.name] = convertCurrencyToFloat(data.lastYear[x.name]?.toString() || "0");
            data.year[x.name] = convertCurrencyToFloat(data.year[x.name]?.toString() || "0");
            data.yearBeforeLast[x.name] = convertCurrencyToFloat(data.yearBeforeLast[x.name]?.toString() || "0");
        });

        return data;
    };

    const handleSubmitFinancialForm = async (data: InvoicingFinepForm) => {
        setLoading(true);
        try {
            data.year.year = year;
            data.year.registerId = registerId ?? "";
            data.year.userEmail = user.userEmail;
            data.lastYear.year = year - 1;
            data.lastYear.registerId = registerId ?? "";
            data.lastYear.userEmail = user.userEmail;
            data.yearBeforeLast.year = year - 2;
            data.yearBeforeLast.registerId = registerId ?? "";
            data.yearBeforeLast.userEmail = user.userEmail;

            const newData = transformFormValuesToNumber(data);
            
            if (getValues('year.id')) {
                await PutInvoicingFinep(newData.year);
                await PutInvoicingFinep(newData.lastYear);
                await PutInvoicingFinep(newData.yearBeforeLast);
            } else {
                await PostInvoicingFinep(newData.year);
                await PostInvoicingFinep(newData.lastYear);
                await PostInvoicingFinep(newData.yearBeforeLast);
            }
            handleFillTab(2);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const debouncedSubmit = debounce(() => {
        handleSubmit(handleSubmitFinancialForm)();
    }, 500);

    const handleInputChange = (value: number | null, name: ObjectFormProps['name'], field: 'year' | 'lastYear' | 'yearBeforeLast') => {
        const path = `${field}.${name}` as FieldPath;
        setValue(path, value || 0);
        debouncedSubmit();
    };

    const handleValidateChange = async (isValid: boolean) => {
        setLoading(true);
        try {
            await PutValidateInvoicingFinep({
                registerId: registerId ?? "",
                isValid: isValid
            });
            setValidateFields(isValid);
            if (isValid) {
                handleValidateTab(2);
            } else {
                handleInvalidateTab(2);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const columns: ColumnsType<ObjectFormProps> = [
        {
            title: <Title className="title-f" level={5} style={{ color: "#000085", fontWeight: "bold", fontSize: "24px" }}>Faturamento</Title>,
            dataIndex: "title",
            key: "title",
            align: "left",
            width: 120,
            render: (text: string, record: ObjectFormProps) => (
                record.h5 ?
                    <Title level={5} style={{ margin: 0 }}><Text strong={record.bold}>{text}</Text></Title> :
                    <Text strong={record.bold}>{text}</Text>
            ),
        },
        {
            title: <Text className="title-f" style={{ color: "#000085", fontWeight: "bold", fontSize: "24px" }}>{year - 2}</Text>,
            dataIndex: "yearBeforeLast",
            key: "yearBeforeLast",
            align: "center",
            width: 60,
            render: (_: any, record: ObjectFormProps) => (
                <div style={{ textAlign: "center" }}>
                    <InputNumber
                        style={{ width: "100%" }}
                        formatter={(value) => `R$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
                        parser={(value) => Number(value?.replace(/[R$\s.]/g, "")) || 0}
                        value={getValues(`yearBeforeLast.${record.name}`)}
                        onChange={(value) => handleInputChange(value, record.name, 'yearBeforeLast')}
                    />
                </div>
            ),
        },
        {
            title: <Text className="title-f" style={{ color: "#000085", fontWeight: "bold", fontSize: "24px" }}>{year - 1}</Text>,
            dataIndex: "lastYear",
            key: "lastYear",
            align: "center",
            width: 60,
            render: (_: any, record: ObjectFormProps) => (
                <div style={{ textAlign: "center" }}>
                    <InputNumber
                        style={{ width: "100%" }}
                        formatter={(value) => `R$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
                        parser={(value) => Number(value?.replace(/[R$\s.]/g, "")) || 0}
                        value={getValues(`lastYear.${record.name}`)}
                        onChange={(value) => handleInputChange(value, record.name, 'lastYear')}
                    />
                </div>
            ),
        },
        {
            title: <Text className="title-f" style={{ color: "#000085", fontWeight: "bold", fontSize: "24px" }}>{year}</Text>,
            dataIndex: "year",
            key: "year",
            align: "center",
            width: 60,
            render: (_: any, record: ObjectFormProps) => (
                <div style={{ textAlign: "center" }}>
                    <InputNumber
                        style={{ width: "100%" }}
                        formatter={(value) => `R$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
                        parser={(value) => Number(value?.replace(/[R$\s.]/g, "")) || 0}
                        value={getValues(`year.${record.name}`)}
                        onChange={(value) => handleInputChange(value, record.name, 'year')}
                    />
                </div>
            ),
        },
    ];

    return (
        <>
            {loading && <Loader />}
            <TabsHeader canValidate valueSelected={validateFields} changeValidate={handleValidateChange} />
            <Table
                className="table-f"
                dataSource={objectForm}
                columns={columns}
                rowKey="name"
                pagination={false}
                bordered={false}
                style={{ marginRight: "250px" }}
                components={{
                    header: {
                        cell: (props: any) => (
                            <th
                                {...props}
                                style={{
                                    border: "none",
                                    background: "transparent",
                                    fontWeight: "bold",
                                    color: "#000085",
                                    textAlign: "center",
                                }}
                            />
                        ),
                    },
                    body: {
                        cell: (props: any) => (
                            <td
                                {...props}
                                style={{
                                    border: "none",
                                    background: "transparent",
                                    textAlign: "start",
                                    padding: "8px 12px",
                                }}
                            />
                        ),
                        row: (props: any) => (
                            <tr {...props} style={{ borderBottom: "none" }} />
                        ),
                    },
                }}
            />
            <div className="d-flex justify-content-end mt-4 mb-4">
                <Button className="me-3" onClick={() => changeTab(1)}>Voltar</Button>
                <Button type="primary" onClick={() => changeTab(3)}> <PlusOutlined /> Próxima Etapa</Button>
            </div>
        </>
    );
};
export default FinancialOpeningsTab;